import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Users,
  AlertCircle,
  Save,
  CheckCircle2,
  Copy,
  Info,
} from 'lucide-react'
import {
  LineupSlot,
  RosterAthleteOption,
  OFFENSE_POSITIONS,
  DEFENSE_POSITIONS,
} from '../../types/sports'
import { Modal } from '../shared/Modal'

interface LineupEditorModalProps {
  isOpen: boolean
  onClose: () => void
  unit: 'offense' | 'defense'
  teamId: string
  initialName?: string
  templateId?: string
  initialSlots?: LineupSlot[]
  athletes: RosterAthleteOption[]
  isCustomLineupMode?: boolean
  onSaveTemplate?: (name: string, slots: { athlete_id: string; position: string }[]) => Promise<void>
  onApplyCustomLineup?: (name: string, slots: LineupSlot[]) => void
}

export const LineupEditorModal: React.FC<LineupEditorModalProps> = ({
  isOpen,
  onClose,
  unit,
  initialName = '',
  templateId,
  initialSlots = [],
  athletes,
  isCustomLineupMode = false,
  onSaveTemplate,
  onApplyCustomLineup,
}) => {
  const positions = useMemo(
    () => (unit === 'offense' ? OFFENSE_POSITIONS : DEFENSE_POSITIONS),
    [unit]
  )
  const defaultName = initialName || (unit === 'offense' ? 'Ataque' : 'Defensa')
  const [name, setName] = useState(defaultName)
  const [slots, setSlots] = useState<{ [pos: string]: string }>(() => {
    const initialMap: { [pos: string]: string } = {}
    positions.forEach((pos) => {
      const found = initialSlots?.find((s) => s.position === pos)
      if (found) {
        initialMap[pos] = found.athlete_id
      }
    })
    return initialMap
  })
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showSaveTemplateOption, setShowSaveTemplateOption] = useState(false)
  const [templateSaveName, setTemplateSaveName] = useState(
    initialName ? `${initialName} (Copia)` : `${unit === 'offense' ? 'Ataque' : 'Defensa'} Nueva`
  )

  // Clave de entidad objetivo para reinicializar sólo si cambia explícitamente la entidad editada
  // sin desmontar (ej. en tests sin key o al alternar de plantilla en caliente)
  const targetEntityKey = `${templateId || 'new'}:${unit}`
  const prevEntityKeyRef = useRef(targetEntityKey)

  useEffect(() => {
    if (prevEntityKeyRef.current !== targetEntityKey) {
      prevEntityKeyRef.current = targetEntityKey
      const currentDefault = initialName || (unit === 'offense' ? 'Ataque' : 'Defensa')
      setName(currentDefault)
      setTemplateSaveName(initialName ? `${initialName} (Copia)` : `${unit === 'offense' ? 'Ataque' : 'Defensa'} Nueva`)
      setErrorMsg(null)
      setShowSaveTemplateOption(false)

      const initialMap: { [pos: string]: string } = {}
      positions.forEach((pos) => {
        const found = initialSlots?.find((s) => s.position === pos)
        if (found) {
          initialMap[pos] = found.athlete_id
        }
      })
      setSlots(initialMap)
    }
  }, [targetEntityKey, initialName, initialSlots, positions, unit])

  if (!isOpen) return null

  // Verificar si hay cambios sin guardar
  const hasUnsavedChanges =
    name !== defaultName ||
    positions.some((pos) => {
      const initialAthId = initialSlots?.find((s) => s.position === pos)?.athlete_id || ''
      return (slots[pos] || '') !== initialAthId
    })

  // Verificar atletas duplicados
  const selectedAthleteIds = Object.values(slots).filter(Boolean)
  const duplicateId = selectedAthleteIds.find(
    (id, idx) => selectedAthleteIds.indexOf(id) !== idx
  )

  const handleSlotChange = (pos: string, athleteId: string) => {
    setErrorMsg(null)
    setSlots((prev) => ({
      ...prev,
      [pos]: athleteId,
    }))
  }

  const validateLineup = (): boolean => {
    if (!name.trim()) {
      setErrorMsg('El nombre de la alineación es obligatorio.')
      return false
    }

    for (const pos of positions) {
      if (!slots[pos]) {
        setErrorMsg(`Debes asignar un jugador para la posición ${pos}.`)
        return false
      }
    }

    const ids = positions.map((p) => slots[p])
    const uniqueIds = new Set(ids)
    if (uniqueIds.size !== 5) {
      setErrorMsg('Un mismo jugador no puede ocupar más de una posición en el quinteto.')
      return false
    }

    return true
  }

  const handleSaveAsTemplate = async () => {
    if (!validateLineup()) return
    const targetName = showSaveTemplateOption ? templateSaveName.trim() : name.trim()
    if (!targetName) {
      setErrorMsg('Introduce un nombre para la nueva plantilla.')
      return
    }

    setIsSaving(true)
    setErrorMsg(null)
    try {
      const slotsPayload = positions.map((pos) => ({
        athlete_id: slots[pos],
        position: pos,
      }))
      if (onSaveTemplate) {
        await onSaveTemplate(targetName, slotsPayload)
      }
      onClose()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message || 'Error al guardar plantilla')
    } finally {
      setIsSaving(false)
    }
  }

  const handleApplyToRep = () => {
    if (!validateLineup()) return

    const compiledSlots: LineupSlot[] = positions.map((pos) => {
      const athId = slots[pos]
      const ath = athletes.find((a) => a.id === athId)
      return {
        athlete_id: athId,
        position: pos,
        athlete_name: ath?.full_name || 'Desconocido',
        dorsal_number: ath?.dorsal_number,
        primary_position: ath?.primary_position,
      }
    })

    if (onApplyCustomLineup) {
      onApplyCustomLineup(name.trim(), compiledSlots)
    }
    onClose()
  }

  const titleText = isCustomLineupMode
    ? `Configurar Quinteto de ${unit === 'offense' ? 'Ataque' : 'Defensa'}`
    : templateId
    ? `Editar Plantilla: ${initialName}`
    : `Nueva Plantilla de ${unit === 'offense' ? 'Ataque' : 'Defensa'}`

  const subtitleText = isCustomLineupMode
    ? 'Modifica los jugadores para esta repetición sin alterar la plantilla original'
    : '5 jugadores distintos con posiciones canónicas para Team Spain'

  const footerNode = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem', width: '100%' }}>
      <button
        type="button"
        onClick={onClose}
        className="btn btn-outline"
        style={{ fontSize: '0.85rem' }}
      >
        Cancelar
      </button>

      {isCustomLineupMode ? (
        <button
          type="button"
          onClick={handleApplyToRep}
          className="btn btn-primary"
          style={{ fontSize: '0.85rem' }}
        >
          <CheckCircle2 size={16} style={{ marginRight: '0.35rem' }} />
          Aplicar a la Repetición
        </button>
      ) : (
        <button
          type="button"
          onClick={handleSaveAsTemplate}
          disabled={isSaving}
          className="btn btn-primary"
          style={{ fontSize: '0.85rem' }}
        >
          <Save size={16} style={{ marginRight: '0.35rem' }} />
          {isSaving ? 'Guardando...' : templateId ? 'Actualizar Plantilla' : 'Guardar Plantilla'}
        </button>
      )}
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      zIndex={1150}
      hasUnsavedChanges={hasUnsavedChanges}
      icon={<Users size={20} />}
      title={titleText}
      subtitle={subtitleText}
      footer={footerNode}
    >
      {/* Alerta de error si existe */}
      {errorMsg && (
        <div
          role="alert"
          style={{
            padding: '0.65rem 0.85rem',
            backgroundColor: 'var(--status-danger-bg)',
            border: '1px solid var(--status-danger-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--status-danger-text)',
            fontSize: '0.84rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0 }} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Nombre del quinteto */}
      <div style={{ marginBottom: '1rem' }}>
        <label
          htmlFor="lineup-name-input"
          style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.35rem', color: 'var(--text-main)' }}
        >
          {isCustomLineupMode ? 'Nombre descriptivo de la alineación:' : 'Nombre de la plantilla:'}
        </label>
        <input
          id="lineup-name-input"
          type="text"
          value={name}
          onChange={(e) => {
            setErrorMsg(null)
            setName(e.target.value)
          }}
          placeholder={unit === 'offense' ? 'Ej. Ataque 1, Ataque Pesado...' : 'Ej. Defensa A, Cover 2 Base...'}
          style={{
            width: '100%',
            padding: '0.55rem 0.75rem',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--surface-card)',
            color: 'var(--text-main)',
            fontSize: '0.9rem',
          }}
        />
      </div>

      {/* Aviso sobre elegibilidad libre */}
      <div
        style={{
          padding: '0.65rem 0.85rem',
          backgroundColor: 'var(--surface-subtle)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.8rem',
          color: 'var(--text-secondary)',
        }}
      >
        <Info size={15} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
        <span>
          La elegibilidad no se restringe por posición principal. Cualquier atleta activo de la plantilla puede ser asignado (ej. un blitzer habitual puede jugar como Z).
        </span>
      </div>

      {/* Slots de las 5 posiciones */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {positions.map((pos) => {
          const currentAthleteId = slots[pos] || ''
          const isDuplicated = duplicateId && currentAthleteId === duplicateId

          return (
            <div
              key={pos}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: isDuplicated ? 'rgba(217, 35, 44, 0.05)' : 'var(--surface-subtle)',
                borderRadius: 'var(--radius-md)',
                border: isDuplicated ? '1px solid var(--brand-red)' : '1px solid var(--surface-border)',
              }}
            >
              <div
                style={{
                  width: '65px',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: unit === 'offense' ? 'var(--brand-red)' : '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <span>{pos}</span>
              </div>

              <select
                value={currentAthleteId}
                onChange={(e) => handleSlotChange(pos, e.target.value)}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.65rem',
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-card)',
                  color: currentAthleteId ? 'var(--text-main)' : 'var(--text-muted)',
                  fontSize: '0.85rem',
                }}
              >
                <option value="">-- Seleccionar jugador --</option>
                {athletes.map((ath) => {
                  let attLabel = ''
                  if (ath.attendance_status === 'confirmed') attLabel = ' [Confirmado]'
                  else if (ath.attendance_status === 'pending') attLabel = ' [Pendiente]'
                  else if (ath.attendance_status === 'declined') attLabel = ' [No asiste]'
                  else if (ath.attendance_status === 'withdrawn') attLabel = ' [Retirado]'
                  else if (ath.attendance_status === 'not_summoned') attLabel = ' [No convocado]'

                  return (
                    <option key={ath.id} value={ath.id}>
                      {ath.dorsal_number ? `#${ath.dorsal_number} ` : ''}
                      {ath.full_name} ({ath.primary_position})
                      {attLabel}
                    </option>
                  )
                })}
              </select>
            </div>
          )
        })}
      </div>

      {/* Opción adicional para guardar como nueva plantilla desde modo personalizado */}
      {isCustomLineupMode && onSaveTemplate && (
        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px dashed var(--surface-border)' }}>
          {!showSaveTemplateOption ? (
            <button
              type="button"
              onClick={() => setShowSaveTemplateOption(true)}
              className="btn btn-outline"
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}
            >
              <Copy size={13} style={{ marginRight: '0.3rem' }} />
              ¿Guardar también esta configuración como plantilla reutilizable?
            </button>
          ) : (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--surface-subtle)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--surface-border)',
              }}
            >
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--text-main)' }}>
                Guardar como nueva plantilla reutilizable:
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={templateSaveName}
                  onChange={(e) => setTemplateSaveName(e.target.value)}
                  placeholder="Nombre de la nueva plantilla"
                  style={{
                    flex: 1,
                    padding: '0.45rem 0.65rem',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--surface-card)',
                    fontSize: '0.85rem',
                  }}
                />
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  disabled={isSaving}
                  className="btn btn-primary"
                  style={{ fontSize: '0.82rem', padding: '0.45rem 0.75rem' }}
                >
                  <Save size={14} style={{ marginRight: '0.25rem' }} />
                  Guardar Plantilla
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
